import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO, isAfter, isBefore, startOfDay } from 'date-fns';
import { nl } from 'date-fns/locale';

const groepColors = {
  'Groen': '#2D6A4F', 'Geel': '#E67E22', 'Rood': '#C0392B',
  'Blauw': '#2980B9', 'Toekomstheld': '#8E44AD'
};

export default function MijnPlanning() {
  const { currentUser } = useAuth();
  const [planning, setPlanning] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('aankomend');

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(
          collection(db, 'planning'),
          where('userId', '==', currentUser.uid)
        ));
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        items.sort((a, b) => (a.datum || '').localeCompare(b.datum || ''));
        setPlanning(items);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [currentUser.uid]);

  const today = startOfDay(new Date());
  const filtered = planning.filter(p => {
    if (!p.datum) return filter === 'alle';
    const d = parseISO(p.datum);
    if (filter === 'aankomend') return !isBefore(d, today);
    if (filter === 'voorbij') return isBefore(d, today);
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">📋 Mijn planning</h1>
        <p className="page-subtitle">Overzicht van al je geplande shiften en taken</p>
      </div>

      <div className="tabs" style={{ maxWidth: 400 }}>
        {[['aankomend', '📅 Aankomend'], ['voorbij', '✅ Voorbij'], ['alle', '📋 Alle']].map(([key, label]) => (
          <button key={key} className={`tab ${filter === key ? 'active' : ''}`} onClick={() => setFilter(key)}>{label}</button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-3)' }}>Laden...</p>
      ) : filtered.length === 0 ? (
        <div className="card card-padded" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Geen planning gevonden</h3>
          <p style={{ color: 'var(--text-3)', fontSize: 14 }}>Er zijn nog geen shiften voor jou ingepland.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(item => {
            const isPast = item.datum && isBefore(parseISO(item.datum), today);
            return (
              <div key={item.id} className="card" style={{
                padding: '16px 20px',
                display: 'flex', alignItems: 'flex-start', gap: 16,
                opacity: isPast ? 0.7 : 1,
                borderLeft: `3px solid ${groepColors[item.groep] || 'var(--primary)'}`
              }}>
                <div style={{
                  background: (groepColors[item.groep] || 'var(--primary)') + '18',
                  borderRadius: 'var(--radius-sm)',
                  padding: '8px 10px',
                  textAlign: 'center',
                  minWidth: 52,
                  flexShrink: 0,
                }}>
                  {item.datum ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' }}>
                        {format(parseISO(item.datum), 'MMM', { locale: nl })}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Syne, sans-serif', color: groepColors[item.groep] || 'var(--primary)' }}>
                        {format(parseISO(item.datum), 'd')}
                      </div>
                    </>
                  ) : <span style={{ fontSize: 20 }}>📅</span>}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <h4 style={{ fontSize: 15, fontWeight: 600 }}>{item.titel || 'Shift'}</h4>
                    {item.groep && (
                      <span className="badge" style={{ background: (groepColors[item.groep] || '#888') + '20', color: groepColors[item.groep] || '#888' }}>
                        {item.groep}
                      </span>
                    )}
                    {isPast && <span className="badge badge-gray">Voorbij</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                    {item.datum && (
                      <span style={{ fontSize: 13, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        📅 {format(parseISO(item.datum), 'EEEE d MMMM yyyy', { locale: nl })}
                      </span>
                    )}
                    {item.locatie && (
                      <span style={{ fontSize: 13, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        📍 {item.locatie}
                      </span>
                    )}
                    {item.tijdVan && (
                      <span style={{ fontSize: 13, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        🕐 {item.tijdVan}{item.tijdTot ? ` – ${item.tijdTot}` : ''}
                      </span>
                    )}
                  </div>
                  {item.notitie && (
                    <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 8, padding: '6px 10px', background: 'var(--surface-3)', borderRadius: 'var(--radius-sm)' }}>
                      {item.notitie}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
